import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, ArrowRight } from 'lucide-react';
import { getBlogById, getPublishedBlogsByProfileId, getUsernameFromProfileId } from '../../../../services/firestoreService';

import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'left',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

/**
 * FeaturedBlogRenderer - Renders a single blog post showcase
 * Two-column layout with cover image and blog info + Read More CTA
 */
const FeaturedBlogRenderer = ({ block, profileId, theme = {}, isOnOwnDomain = false }) => {
  const { content } = block;
  const {
    blogId,
    buttonText = 'Read More',
    showExcerpt = true,
    layout = 'imageLeft',
  } = content;

  // Backwards-compat: blocks created before contentMode existed are treated as
  // 'specific' when they have a blogId, otherwise 'most-recent'.
  const contentMode = content.contentMode || (blogId ? 'specific' : 'most-recent');

  const [blog, setBlog] = useState(null);
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(true);

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  useEffect(() => {
    const loadBlog = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      if (contentMode === 'specific' && !blogId) {
        setLoading(false);
        return;
      }

      try {
        const usernamePromise = getUsernameFromProfileId(profileId);
        const blogPromise =
          contentMode === 'most-recent'
            ? getPublishedBlogsByProfileId(profileId).then((blogs) => blogs[0] || null)
            : getBlogById(blogId);

        const [fetchedBlog, fetchedUsername] = await Promise.all([blogPromise, usernamePromise]);

        if (fetchedBlog) {
          setBlog(fetchedBlog);
        }
        setUsername(fetchedUsername);
      } catch (error) {
        console.error('Error loading blog:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBlog();
  }, [contentMode, blogId, profileId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp?.toDate?.() || new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Loading state
  if (loading) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse rounded-xl overflow-hidden border border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="bg-gray-800 h-64" />
              <div className="p-6 space-y-4">
                <div className="h-8 bg-gray-800 rounded w-3/4" />
                <div className="h-4 bg-gray-800 rounded w-1/3" />
                <div className="h-4 bg-gray-800 rounded w-full" />
                <div className="h-4 bg-gray-800 rounded w-5/6" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No blog selected or blog not found
  if (!blog) {
    let emptyMessage;
    if (contentMode === 'most-recent') {
      emptyMessage = 'No published blog posts yet';
    } else if (blogId) {
      emptyMessage = 'Blog post not found';
    } else {
      emptyMessage = 'No blog post selected';
    }
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <Newspaper size={48} className="mx-auto mb-4" style={{ color: styles.mutedColor }} />
          <p style={{ color: styles.mutedColor }}>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  const blogUrl = username
    ? (isOnOwnDomain ? `/blog/${blog.slug || blog.id}` : `/me/${username}/blog/${blog.slug || blog.id}`)
    : null;

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Blog Card */}
        <div
          className="rounded-xl"
          style={{
            backgroundColor: styles.cardBackground,
            border: `1px solid ${styles.borderColor}`,
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 items-center">
            {/* Image column */}
            <div className={`${layout === 'imageLeft' ? 'md:order-1' : 'md:order-2'} flex justify-center p-6`}>
              {blog.imageUrl ? (
                <img
                  src={blog.imageUrl}
                  alt={blog.title}
                  className="w-full h-auto object-cover rounded-xl"
                  style={{ maxHeight: '400px', maxWidth: 'min(400px, 100%)' }}
                />
              ) : (
                <div
                  className="w-full h-56 flex items-center justify-center"
                  style={{ backgroundColor: styles.sectionBackground }}
                >
                  <Newspaper size={48} style={{ color: styles.mutedColor }} />
                </div>
              )}
            </div>

            {/* Info column */}
            <div className={`${layout === 'imageLeft' ? 'md:order-2' : 'md:order-1'} p-6 sm:p-8 space-y-3`}>
              <h4
                className="text-xl sm:text-2xl md:text-3xl font-bold"
                style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
              >
                {blog.title}
              </h4>

              <p
                className="text-sm"
                style={{ color: styles.mutedColor }}
              >
                {formatDate(blog.publishedAt || blog.createdAt)}
              </p>

              {showExcerpt && blog.excerpt && (
                <p
                  className="text-sm sm:text-base line-clamp-4"
                  style={{ color: styles.bodyColor, fontFamily: styles.bodyFontStyle }}
                >
                  {blog.excerpt}
                </p>
              )}

              {/* Read More button */}
              {blogUrl && (
                <div className="pt-2">
                  <Link
                    to={blogUrl}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-base transition-colors hover:opacity-90"
                    style={styles.getButtonStyles()}
                  >
                    <Newspaper size={18} />
                    {buttonText}
                    <ArrowRight size={16} />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedBlogRenderer;
